import { useCallback, useMemo } from 'react';
import { useStore } from '../store';
import { send } from '../ws';
import { getWs } from '../wsConnection';
import { waitForAck } from '../utils/waitForAck';
import { correlationId } from "@interlucent/ids";
import type {
  LocalVersionCreateReq,
  LocalVersionUpdateReq,
  LocalVersionCreateAck,
  LocalVersionUpdateAck,
  LocalVersionGetTokensAck,
  LocalVersionCycleTokensAck,
  AppVersion,
  AckPayload
} from '@interlucent/organization-protocol';

export interface LocalVersionTokens {
  browserAdmissionToken: string;
  workerAdmissionToken: string;
  localVersionId: string;
  expiresAt: string;
}

/**
 * Custom hook for local version CRUD operations
 * Abstracts WebSocket commands and store updates
 */
export function useLocalVersions(appId: string) {
  const versions = useStore(state => state.versions);

  // Get local versions for the current application
  const localVersions = useMemo(() => {
    return Object.values(versions).filter(
      (v: AppVersion) => v.appId === appId && v.isLocal === true
    );
  }, [versions, appId]);

  /**
   * Create a new local version
   */
  const createLocalVersion = useCallback(async (
    data: Omit<LocalVersionCreateReq, 'appId'>
  ): Promise<{ success: boolean; versionId?: string; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: LocalVersionCreateReq = {
        ...data,
        appId
      };

      send('localVersion.create', payload, corr);

      const ack = await waitForAck<LocalVersionCreateAck>(corr);

      if (ack.ok) {
        return {
          success: true,
          versionId: ack.id
        };
      } else {
        return {
          success: false,
          error: 'Failed to create local version'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to create local version'
      };
    }
  }, [appId]);

  /**
   * Update an existing local version
   */
  const updateLocalVersion = useCallback(async (
    versionId: string,
    updates: Omit<LocalVersionUpdateReq, 'id'>
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      const payload: LocalVersionUpdateReq = {
        ...updates,
        id: versionId
      };

      send('localVersion.update', payload, corr);

      const ack = await waitForAck<LocalVersionUpdateAck>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to update local version'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to update local version'
      };
    }
  }, []);

  /**
   * Delete a local version
   */
  const deleteLocalVersion = useCallback(async (
    versionId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('localVersion.delete', { id: versionId }, corr);

      const ack = await waitForAck<AckPayload>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: (ack as any).reason || 'Failed to delete local version'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to delete local version'
      };
    }
  }, []);

  /**
   * Get tokens for a local version
   */
  const getTokens = useCallback(async (
    versionId: string
  ): Promise<{ success: boolean; tokens?: LocalVersionTokens; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('localVersion.getTokens', { id: versionId }, corr);

      const ack = await waitForAck<LocalVersionGetTokensAck>(corr);

      if (ack.ok) {
        return {
          success: true,
          tokens: {
            browserAdmissionToken: ack.browserAdmissionToken,
            workerAdmissionToken: ack.workerAdmissionToken,
            localVersionId: ack.localVersionId,
            expiresAt: ack.expiresAt
          }
        };
      } else {
        return {
          success: false,
          error: 'Failed to get tokens'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to get tokens'
      };
    }
  }, []);

  /**
   * Cycle (regenerate) tokens for a local version
   */
  const cycleTokens = useCallback(async (
    versionId: string
  ): Promise<{ success: boolean; error?: string }> => {
    if (!getWs()) {
      return { success: false, error: 'WebSocket connection not available' };
    }

    try {
      const corr = correlationId();
      send('localVersion.cycleTokens', { id: versionId }, corr);

      const ack = await waitForAck<LocalVersionCycleTokensAck>(corr);

      if (ack.ok) {
        return { success: true };
      } else {
        return {
          success: false,
          error: 'Failed to cycle tokens'
        };
      }
    } catch (error: any) {
      return {
        success: false,
        error: error.message || 'Failed to cycle tokens'
      };
    }
  }, []);

  return {
    localVersions,
    createLocalVersion,
    updateLocalVersion,
    deleteLocalVersion,
    getTokens,
    cycleTokens,
  };
}
